import { Injectable, inject } from '@angular/core';
import {
  CoordinateSystem,
  GeoBrowserService,
  InteractionService,
  MapColors,
} from '@next/arable-lib/geo';
import { Store, select } from '@ngrx/store';
import { EventsKey } from 'ol/events';
import { never } from 'ol/events/condition';
import { GeoJSON } from 'ol/format';
import { Select } from 'ol/interaction';
import { Vector as Layer } from 'ol/layer';
import { unByKey } from 'ol/Observable';
import { Vector } from 'ol/source';
import { Style, Fill, Stroke } from 'ol/style';
import { combineLatest } from 'rxjs';
import { SiteState } from 'src/app/shared/models';
import { SoilAnalysisSortHelper } from 'src/app/soil-analysis/helpers/SoilAnalysisSortHelper';
import {
  deselectSoilAnalysisFeature,
  selectMultipleSoilAnalysisFeatures,
} from 'src/app/soil-analysis/store/soil-analysis.actions';
import * as SoilAnalysisSelectors from 'src/app/soil-analysis/store/soil-analysis.selectors';

import { InteractionType } from '../../models';

import { getSoilAnalysesEnabled } from '../../store/selectors/map-context.selectors';

@Injectable({
  providedIn: 'root',
})
export class GeoSelectSoilAnalysisService implements InteractionService {
  private readonly store = inject<Store<SiteState>>(Store);

  interactionTypes = [InteractionType.selectSoilAnalysis];
  private selectEventsKey: EventsKey;
  private reader = new GeoJSON({
    dataProjection: CoordinateSystem.WorldGeodetic,
    featureProjection: CoordinateSystem.PseudoMercator,
  });

  readonly source = new Vector({
    wrapX: false,
  });

  readonly style = new Style({
    fill: new Fill({
      color: MapColors.soilAnalysesFill,
    }),
  });

  readonly activeStyle = new Style({
    stroke: new Stroke({
      color: MapColors.soilAnalysesStroke,
      width: 1.5,
    }),
    fill: new Fill({
      color: MapColors.soilAnalysesFill,
    }),
  });

  readonly selectedStyle = new Style({
    stroke: new Stroke({
      color: MapColors.soilAnalysesSelectedStroke,
      width: 2.5,
    }),
    fill: new Fill({
      color: MapColors.soilAnalysesSelectedFill,
    }),
  });

  readonly layer = new Layer({
    source: this.source,
    style: this.style,
    zIndex: 2,
  });

  readonly select = new Select({
    toggleCondition: never,
    multi: true,
    layers: (layer) => layer === this.layer,
    style: this.selectedStyle,
  });

  private readonly selectFeatures = this.select.getFeatures();

  init(geoService: GeoBrowserService) {
    this.select.setActive(false);
    geoService.map.addLayer(this.layer);
    geoService.map.addInteraction(this.select);

    this.store.pipe(select(SoilAnalysisSelectors.selectSoilAnalyses)).subscribe((features) => {
      this.source.clear();

      const sortedFeatures = SoilAnalysisSortHelper.sortAsc(features);

      sortedFeatures
        .filter((featureEntity) => featureEntity.borderData)
        .forEach((featureEntity) => {
          const feature = this.reader.readFeature(featureEntity.borderData);
          feature.setId(featureEntity.borderData.id);
          this.source.addFeature(feature);
        });
    });

    combineLatest([
      this.store.pipe(select(SoilAnalysisSelectors.selectSelectedFeatureIds)),
      this.store.pipe(select(SoilAnalysisSelectors.selectAllFeatureIds)),
    ]).subscribe(([selectedIds, ids]) => {
      const unselectedIds = ids.filter((id) => selectedIds.indexOf(id) === -1);
      const selectedFeatures = selectedIds.map((id) => this.source.getFeatureById(id));
      const unselectedFeatures = unselectedIds.map((id) => this.source.getFeatureById(id));

      selectedFeatures
        .filter((feature) => this.selectFeatures.getArray().indexOf(feature) === -1)
        .forEach((feature) => this.selectFeatures.push(feature));

      unselectedFeatures
        .filter((feature) => this.selectFeatures.getArray().indexOf(feature) !== -1)
        .forEach((feature) => {
          this.selectFeatures.remove(feature);
        });
    });

    this.store.pipe(select(getSoilAnalysesEnabled)).subscribe((enabled) => {
      this.layer.setVisible(enabled);
    });
  }

  destroy(geoService: GeoBrowserService) {
    geoService.map.removeLayer(this.layer);
    geoService.map.removeInteraction(this.select);
  }

  start(_: GeoBrowserService) {
    this.selectEventsKey = this.select.on('select', (selectEvent) => {
      this.store.dispatch(
        selectMultipleSoilAnalysisFeatures({
          featureIds: selectEvent.selected.map((feature) => feature.getId() as string),
        }),
      );
      selectEvent.deselected.forEach((feature) => {
        this.store.dispatch(deselectSoilAnalysisFeature({ featureId: feature.getId() as string }));
      });
    });

    this.select.setActive(true);
    this.layer.setStyle(this.activeStyle);
  }

  stop(_: GeoBrowserService) {
    unByKey(this.selectEventsKey);
    this.select.setActive(false);
    this.layer.setStyle(this.style);
  }
}
